// resources/js/src/services/auth.api.ts
import { RegistrationValues } from "../components/register/types/registration";
import { request } from "./api";

// ============ Types ============

export type UserRole = "user" | "company" | "admin" | "superadmin";

export interface User {
  id: string | number;
  name: string;
  email: string;
  role: UserRole;
  accountType?: string;
  status?: boolean;
  avatarUrl?: string | null;
  coverUrl :string | null;
  bio:string | null;
}

/** الشكل الخام اللي جاي من الـ backend */
export type AuthUserResponse = {
  id: number | string;
  name?: string;
  fullName?: string;
  email: string;
  role?: string;
  status?: boolean;
  avatarUrl?: string | null;
  accountType?: string;
  // حقول إضافية اختيارية موجودة في /auth/me
  profile?: {
    gender?: string | null;
    phone?: string | null;
  };
  company?: Record<string, unknown>;
  emailVerified?: boolean;
  emailVerifiedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
};

// ============ Response shapes (بعد ما request() تاخد .data) ============

/** GET /auth/me → الـ user مباشرة */
export type MeResponse = AuthUserResponse;

/** POST /auth/login → { user: {...} } */
export type LoginResponse = {
  user: AuthUserResponse;
};

/** POST /auth/verify-email → { user: {...} } */
export type VerifyResponse = {
  verified: boolean;
  user: AuthUserResponse;
};

/** POST /auth/google → { user: {...}, isNewUser? } */
export type GoogleAuthResponse = {
  user: AuthUserResponse;
  isNewUser?: boolean;
};

/** POST /auth/register → { email, verificationCode? } */
export type RegisterResponse = {
  email: string;
  verificationCode?: string | null;
};

/** POST /auth/resend-verification → { verificationCode? } */
export type ResendVerificationResponse = {
  verificationCode?: string | null;
};

// ============ API ============

export const authApi = {
  register: (payload: FormData) =>
    request<RegisterResponse>("POST", "/auth/register", payload),

  login: (email: string, password: string) =>
    request<LoginResponse>("POST", "/auth/login", { email, password }),

  verifyEmail: (email: string, code: string) =>
    request<VerifyResponse>("POST", "/auth/verify-email", { email, code }),

  resendVerification: (email: string) =>
    request<ResendVerificationResponse>(
      "POST",
      "/auth/resend-verification",
      { email }
    ),

  forgotPassword: (email: string) =>
    request<{ sent: boolean }>("POST", "/auth/forgot-password", { email }),

  googleAuth: (accessToken: string) =>
    request<GoogleAuthResponse>("POST", "/auth/google", { accessToken }),

  me: () => request<MeResponse>("GET", "/auth/me"),
};

/* ============ Helpers ============ */

export function normalizeUser(raw: any): User {
  return {
    id: raw.id,
    name:
      raw.name ??
      raw.fullName ??
      raw.email?.split("@")[0] ??
      "User",
    email: raw.email,
    role: (raw.role ?? "user") as UserRole,
    accountType: raw.accountType ?? "individual",
    status: raw.status ?? true,
    avatarUrl: raw.avatarUrl ?? null,
    bio: raw.bio ?? null,
    coverUrl: raw.coverUrl ?? null
  };
}

export function registrationToFormData(data: RegistrationValues): FormData {
  const formData = new FormData();

  Object.entries(data).forEach(([key, value]) => {
    if (value === null || value === undefined) return;

    if (value instanceof File) {
      formData.append(key, value);
      return;
    }

    if (Array.isArray(value) && value.every((v) => v instanceof File)) {
      value.forEach((file) => formData.append(`${key}[]`, file));
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((v) => formData.append(`${key}[]`, String(v)));
      return;
    }

    if (typeof value === "boolean") {
      formData.append(key, value ? "1" : "0");
      return;
    }

    formData.append(key, String(value));
  });

  return formData;
}